// Dev-only mock of evisio2 SDK Connection. Fulfills the Connection contract from
// evisio2sdk.d.ts so Evisio2SdkWiring can connect against it transparently. State
// lives entirely in-process; the simulator widget drives the control surface
// (_setUserHash / _emit / _transitionFileStatus / _setCapabilities / ...).

import type {
    AttachFileInput,
    AttachFileResult,
    ConnectionEventMap,
    ConnectionState,
    Disposer,
    ExternalToolsSkillInfo,
    FetchedArtifact,
    FileInfo,
    SdkCapabilities,
    Tool,
    ViewOptions,
} from "../../sdk/evisio2sdk";
import { MockEvisioError } from "./mockEvisioError";

// Channels emitted by `_notifyChange` so simulator subwidgets can refresh selectively.
// The simulator subscribes via `addStateChangeListener` — separate from the SDK's
// `on(event, cb)` subscriber bag (which is for ConnectionEventMap events the SDK
// would fire over the wire).
export type MockStateChannel = "tools" | "files" | "view" | "mode" | "state" | "thread" | "userHash" | "capabilities";

// Internal representation of a thread file. Superset of FileInfo with bytes attached
// and a force-reject-detach flag the Files tab can toggle for error-path testing.
interface MockThreadFile {
    id: string;
    name: string;
    mime: string;
    direction: "input" | "output" | "toolResult";
    status: "uploaded" | "processing" | "ready" | "failed";
    available: boolean;
    bytes: ArrayBuffer;
    failReason?: string;
    forceRejectDetach?: boolean;
}

class MockEvisio2Connection {
    private _listeners: { [K in keyof ConnectionEventMap]?: Set<(data: any) => void> } = {};
    private _stickyReady: boolean = false;
    private _stickySignedIn: ConnectionEventMap["signedIn"] | null = null;

    private _stateChangeListeners: Set<(channel: MockStateChannel) => void> = new Set();

    private _registeredTools: Map<string, Tool> = new Map();
    private _skillInfo: ExternalToolsSkillInfo | null = null;
    private _threadFiles: Map<string, MockThreadFile> = new Map();
    private _view: ViewOptions = {};
    private _modeId: string | null = null;
    private _currentThreadId: string | null = null;
    private _state: ConnectionState = "connecting";
    private _userHash: string | null = null;
    private _capabilities: SdkCapabilities = { files: true, maxUploadBytes: undefined };
    private _mode: "iframe" | "iframeAttach" | "popup";

    // Counter for synthetic file ids. Auto-increments per attach/inject so each file
    // has a stable, predictable id during a session.
    private _fileIdCounter: number = 0;

    constructor(connectMode: "iframe" | "iframeAttach" | "popup", initialView: ViewOptions, tools: Tool[]){
        this._mode = connectMode;
        if (initialView) this._view = Object.assign({}, initialView);
        if (tools && tools.length){
            for (let i = 0; i < tools.length; i++){ this._registeredTools.set(tools[i].name, tools[i]); }
        }
    }

    // ─── Connection interface (readonly accessors) ──────────────────────────

    get state(): ConnectionState { return this._state; }
    get userHash(): string | null { return this._userHash; }
    get mode(): "iframe" | "iframeAttach" | "popup" { return this._mode; }
    get capabilities(): SdkCapabilities { return this._capabilities; }

    // ─── Connection interface (mutators) ────────────────────────────────────

    registerTools(tools: Tool[]): void {
        for (let i = 0; i < tools.length; i++){ this._registeredTools.set(tools[i].name, tools[i]); }
        this._notifyChange("tools");
    }

    removeTools(names: string[]): void {
        for (let i = 0; i < names.length; i++){ this._registeredTools.delete(names[i]); }
        this._notifyChange("tools");
    }

    setSkillInfo(info: ExternalToolsSkillInfo): void {
        // Presentation-only in the real SDK (skill chip / palette labels) — the mock just
        // records the last value so the simulator can inspect it.
        this._skillInfo = info || null;
    }

    setView(options: Partial<ViewOptions>): void {
        if (!options) return;
        let keys: (keyof ViewOptions)[] = ["compactSidebar", "theme", "uiLanguage", "aiLanguage"];
        for (let i = 0; i < keys.length; i++){
            let k = keys[i];
            let v = (options as any)[k];
            if (v !== undefined) (this._view as any)[k] = v;
        }
        this._notifyChange("view");
    }

    setMode(modeId: string | null): void {
        this._modeId = modeId;
        this._notifyChange("mode");
    }

    openThread(threadId: string | null): void {
        let normalized = threadId;
        if (normalized === "" ) normalized = null;
        this._currentThreadId = normalized;
        this._notifyChange("thread");
    }

    focus(): void {
        console.log("[mockEvisio2Connection] focus() called (no-op in mock)");
    }

    async attachFile(input: AttachFileInput): Promise<AttachFileResult> {
        if (!this._capabilities.files) throw MockEvisioError.unsupported("attachFile: capabilities.files === false");
        if (this._capabilities.maxUploadBytes !== undefined && input.bytes.byteLength > this._capabilities.maxUploadBytes){
            throw MockEvisioError.fileTooLarge("attachFile: bytes " + input.bytes.byteLength + " > cap " + this._capabilities.maxUploadBytes);
        }
        let fileId = this._mintFileId();
        let mime = input.mime || "application/octet-stream";
        this._threadFiles.set(fileId, {
            id: fileId,
            name: input.name,
            mime: mime,
            direction: "input",
            status: "processing",
            available: true,
            bytes: input.bytes,
        });
        this._notifyChange("files");
        return { fileId: fileId, status: "processing" };
    }

    async fetchArtifact(fileId: string): Promise<FetchedArtifact> {
        if (!this._capabilities.files) throw MockEvisioError.unsupported("fetchArtifact: capabilities.files === false");
        let f = this._threadFiles.get(fileId);
        if (!f) throw MockEvisioError.notFound("fetchArtifact: " + fileId);
        if (!f.available) throw MockEvisioError.fileCleaned("fetchArtifact: " + fileId);
        return { name: f.name, mime: f.mime, bytes: f.bytes.slice(0) };
    }

    async listThreadFiles(): Promise<FileInfo[]> {
        if (!this._capabilities.files) throw MockEvisioError.unsupported("listThreadFiles: capabilities.files === false");
        let out: FileInfo[] = [];
        this._threadFiles.forEach((f) => {
            out.push({ id: f.id, name: f.name, mime: f.mime, direction: f.direction, status: f.status, available: f.available });
        });
        return out;
    }

    async detachFile(fileId: string): Promise<void> {
        if (!this._capabilities.files) throw MockEvisioError.unsupported("detachFile: capabilities.files === false");
        let f = this._threadFiles.get(fileId);
        if (f && f.forceRejectDetach) throw MockEvisioError.detachFailed("detachFile: " + fileId + " (forced reject)", { code: "force_reject" });
        this._threadFiles.delete(fileId); // no-op if missing — matches real contract
        this._notifyChange("files");
    }

    on<K extends keyof ConnectionEventMap>(event: K, cb: (data: ConnectionEventMap[K]) => void): Disposer {
        let set = this._listeners[event];
        if (!set){ set = new Set(); this._listeners[event] = set; }
        set.add(cb as any);

        // Sticky replay for ready + signedIn — fires in a microtask so attach order
        // doesn't matter to the consumer.
        if (event === "ready" && this._stickyReady){
            Promise.resolve().then(() => { try { (cb as any)({}); } catch (e){ console.warn("[mockEvisio2Connection] sticky ready cb threw", e); } });
        }
        if (event === "signedIn" && this._stickySignedIn){
            let val = this._stickySignedIn;
            Promise.resolve().then(() => { try { (cb as any)(val); } catch (e){ console.warn("[mockEvisio2Connection] sticky signedIn cb threw", e); } });
        }

        return () => { let s = this._listeners[event]; if (s) s.delete(cb as any); };
    }

    close(): void {
        if (this._state === "disconnected") return;
        this._state = "disconnected";
        this._notifyChange("state");
        this._emit("disconnected", {});
        // Drop listeners after the final disconnected fan-out — consumers shouldn't see
        // any further events for this connection instance.
        this._listeners = {};
    }

    // ─── Dev-only control surface (NOT part of Connection contract) ─────────
    //
    // The simulator widget calls these to drive state from the UI. Underscore-prefix
    // marks them as out-of-contract so anyone reading the mock through the Connection
    // type doesn't see them.

    // Emit a connection event by name. Sticky semantics handled here for ready/signedIn/signedOut.
    public _emit<K extends keyof ConnectionEventMap>(event: K, payload: ConnectionEventMap[K]): void {
        if (event === "ready") this._stickyReady = true;
        if (event === "signedIn"){ this._stickySignedIn = payload as any; this._userHash = (payload as any).userHash || null; this._notifyChange("userHash"); }
        if (event === "signedOut"){ this._stickySignedIn = null; this._userHash = null; this._notifyChange("userHash"); }
        if (event === "connectionLost"){ this._state = "suspended"; this._notifyChange("state"); }
        if (event === "resumed"){ this._state = "connected"; this._notifyChange("state"); }
        if (event === "disconnected"){ this._state = "disconnected"; this._notifyChange("state"); }
        if (event === "threadJoined"){ this._currentThreadId = (payload as any).threadId; this._notifyChange("thread"); }

        let set = this._listeners[event];
        if (!set) return;
        set.forEach((cb) => {
            try { cb(payload as any); } catch (e){ console.warn("[mockEvisio2Connection] listener threw for " + String(event), e); }
        });
    }

    // Snapshot of the current state — used by the State tab and JSON-dump button.
    public _snapshot(): {
        state: ConnectionState;
        userHash: string | null;
        mode: "iframe" | "iframeAttach" | "popup";
        capabilities: SdkCapabilities;
        view: ViewOptions;
        modeId: string | null;
        currentThreadId: string | null;
        registeredTools: string[];
        threadFiles: { id: string; name: string; mime: string; status: string; available: boolean; direction: string }[];
    } {
        let files: { id: string; name: string; mime: string; status: string; available: boolean; direction: string }[] = [];
        this._threadFiles.forEach(f => files.push({ id: f.id, name: f.name, mime: f.mime, status: f.status, available: f.available, direction: f.direction }));
        let toolNames: string[] = [];
        this._registeredTools.forEach((_v, k) => toolNames.push(k));
        return {
            state: this._state,
            userHash: this._userHash,
            mode: this._mode,
            capabilities: Object.assign({}, this._capabilities),
            view: Object.assign({}, this._view),
            modeId: this._modeId,
            currentThreadId: this._currentThreadId,
            registeredTools: toolNames,
            threadFiles: files,
        };
    }

    // Tools tab needs to enumerate registered tools + invoke handlers directly.
    public _getRegisteredTools(): Tool[] {
        let out: Tool[] = [];
        this._registeredTools.forEach(t => out.push(t));
        return out;
    }
    public _getRegisteredTool(name: string): Tool | undefined {
        return this._registeredTools.get(name);
    }

    // Files tab — inject a host-side file directly (skips the attachFile path so the
    // file appears with chosen initial status, useful for testing 'ready' or 'failed'
    // without needing to step the FSM through transitions).
    public _injectFile(input: { name: string; mime: string; bytes: ArrayBuffer; direction?: "input" | "output" | "toolResult"; status?: "uploaded" | "processing" | "ready" | "failed"; available?: boolean; failReason?: string }): string {
        let fileId = this._mintFileId();
        this._threadFiles.set(fileId, {
            id: fileId,
            name: input.name,
            mime: input.mime,
            direction: input.direction || "input",
            status: input.status || "uploaded",
            available: input.available !== false,
            bytes: input.bytes,
            failReason: input.failReason,
        });
        this._notifyChange("files");
        return fileId;
    }
    public _transitionFileStatus(fileId: string, status: "uploaded" | "processing" | "ready" | "failed", reason?: string): void {
        let f = this._threadFiles.get(fileId);
        if (!f) return;
        f.status = status;
        if (status === "failed") f.failReason = reason || "";
        else f.failReason = undefined;
        this._notifyChange("files");
        // Real SDK fires fileStatus + fileReady (subset) + fileCleaned. Mock fires fileStatus
        // for every transition; fileReady on ready; nothing extra for failed (consumer should
        // read fileStatus.reason).
        this._emit("fileStatus", { fileId: fileId, status: status, reason: reason || null });
        if (status === "ready") this._emit("fileReady", { fileId: fileId });
    }
    public _markFileCleaned(fileId: string): void {
        let f = this._threadFiles.get(fileId);
        if (!f) return;
        f.available = false;
        this._notifyChange("files");
        this._emit("fileCleaned", { fileId: fileId });
    }
    public _setFileForceRejectDetach(fileId: string, force: boolean): void {
        let f = this._threadFiles.get(fileId);
        if (!f) return;
        f.forceRejectDetach = force;
        this._notifyChange("files");
    }
    public _removeFile(fileId: string): void {
        if (this._threadFiles.delete(fileId)) this._notifyChange("files");
    }
    public _getFile(fileId: string): MockThreadFile | undefined {
        return this._threadFiles.get(fileId);
    }
    public _getAllFiles(): MockThreadFile[] {
        let out: MockThreadFile[] = [];
        this._threadFiles.forEach(f => out.push(f));
        return out;
    }

    public _setCapabilities(caps: { files?: boolean; maxUploadBytes?: number | null }): void {
        if (caps.files !== undefined) this._capabilities.files = caps.files;
        if (caps.maxUploadBytes === null){ this._capabilities.maxUploadBytes = undefined; }
        else if (caps.maxUploadBytes !== undefined){ this._capabilities.maxUploadBytes = caps.maxUploadBytes; }
        this._notifyChange("capabilities");
    }

    public _setState(state: ConnectionState): void {
        this._state = state;
        this._notifyChange("state");
    }

    public _setUserHash(userHash: string | null): void {
        this._userHash = userHash;
        this._notifyChange("userHash");
    }

    public _getView(): ViewOptions { return Object.assign({}, this._view); }
    public _getModeId(): string | null { return this._modeId; }
    public _getCurrentThreadId(): string | null { return this._currentThreadId; }

    // Simulator subwidgets subscribe here so they can refresh on state changes.
    public addStateChangeListener(cb: (channel: MockStateChannel) => void): Disposer {
        this._stateChangeListeners.add(cb);
        return () => { this._stateChangeListeners.delete(cb); };
    }

    private _notifyChange(channel: MockStateChannel): void {
        this._stateChangeListeners.forEach(cb => {
            try { cb(channel); } catch (e){ console.warn("[mockEvisio2Connection] state-change listener threw", e); }
        });
    }

    private _mintFileId(): string {
        this._fileIdCounter += 1;
        // Format mirrors what a real server might emit — no claim of uniqueness across sessions,
        // just stable within one mock-mode session.
        return "mock-file-" + String(this._fileIdCounter).padStart(4, "0");
    }
}

export { MockEvisio2Connection };
export type { MockThreadFile };
